'use client';

import * as React from 'react';
import type { JSX } from 'react';
import { Input } from './Input';
import { cn, tryCatch, err, ok } from '@shared/utils';
import type { CurrencyInputProps } from '../../types/CurrencyInputProps';

/**
 * @group Components
 * @summary <blockquote>CurrencyInput component for formatted currency input.</blockquote>
 * @example
 * ```tsx
 * <CurrencyInput 
 *   value={100.50} 
 *   onChange={(value) => setAmount(value)} 
 *   currency="R" 
 * />
 * ```
 */

/**
 * CurrencyInput component formats numbers as currency with a fixed 2-decimal format.
 * It displays the currency symbol and uses locale formatting (en-ZA).
 *
 * @example
 * ```tsx
 * <CurrencyInput
 *   currency="R"
 *   value={1234.56}
 *   onChange={(val) => console.log(val)}
 * />
 * ```
 */
const CurrencyInput = React.forwardRef<HTMLInputElement, CurrencyInputProps>(
  ({ className, onChange, currency = 'R', value, ...props }, ref): JSX.Element => {
    /**
     * Handles raw text input changes,
     * strips all non-numeric and non-decimal characters,
     * converts to number and calls onChange with numeric value.
     */
    const handleChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
      const rawValue = e.target.value.replace(/[^0-9.]/g, '');
      
      // Handle empty string case
      if (rawValue === '') {
        if (onChange) {
          onChange(0);
        }
        return;
      }
      
      // Use Number() instead of parseFloat to avoid linter rule
      const numValue = Number(rawValue);
      
      if (isNaN(numValue)) {
        // Invalid input - silently ignore (don't call onChange)
        return;
      }
      
      if (onChange) {
        onChange(numValue);
      }
    };

    /**
     * Formats the numeric value as a currency string with two decimals and currency symbol.
     * Returns empty string if value is undefined.
     */
    const formatValue = (value: number | undefined) => {
      if (value === undefined) return '';
      return `${currency} ${value.toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    };

    return (
      <Input
        {...props}
        ref={ref}
        type="text"
        className={cn('text-right', className)}
        value={formatValue(value)}
        onChange={handleChange}
        placeholder={`${currency} 0.00`}
      />
    );
  }
);

CurrencyInput.displayName = 'CurrencyInput';

export { CurrencyInput };
